import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { forgotPassword, formatApiError } from "../lib/authApi";

export const ForgotPasswordModal = ({ open, onClose, onBackToLogin, defaultEmail = "" }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] p-4 bg-slate-900/50 backdrop-blur-sm overflow-y-auto"
      data-testid="forgot-modal-overlay"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center">
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-4"
          onClick={(e) => e.stopPropagation()}
          data-testid="forgot-modal"
        >
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">
                {sent ? "Check your inbox" : "Reset your password"}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {sent
                  ? "If an account exists for this email, we've sent a reset link. It's valid for 60 minutes."
                  : "Enter the email on your account and we'll send you a secure link to set a new password."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="forgot-close"
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-6">
          {sent ? (
            <div className="space-y-4">
              <div
                className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2.5 text-sm"
                data-testid="forgot-success"
              >
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Email sent to <strong>{email}</strong>. Check your spam folder if you don't see it in the next couple of minutes.
                </span>
              </div>
              <button
                onClick={onBackToLogin}
                data-testid="forgot-back-to-login"
                className="w-full inline-flex items-center justify-center gap-2 border border-slate-200 hover:border-slate-300 text-slate-700 rounded-full py-2.5 text-sm font-medium"
              >
                Back to log in
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Email</span>
                <div className="mt-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus-within:border-blue-400 focus-within:bg-white">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
                    placeholder="you@example.com"
                    data-testid="forgot-email"
                  />
                </div>
              </label>

              {error && (
                <div
                  className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                  data-testid="forgot-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-full py-2.5 text-sm font-medium shadow-sm"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </button>

              <button
                type="button"
                onClick={onBackToLogin}
                data-testid="forgot-cancel"
                className="w-full text-sm text-slate-500 hover:text-slate-800 py-1"
              >
                Back to log in
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>,
    document.body,
  );
};

export default ForgotPasswordModal;
